import React, { useEffect, useState, useRef } from 'react';
import { GetConfig, RegisterAttendance } from '../../../utilities/BL/Attendance';
import { ConnectWebSocketForOverlay, DisconnectWebSocket, SetSocketMessageHandler, ClearSocketMessageHandler } from './../../../utilities/BL/Zones';

const AttendanceScreen = () => {
    const [config, setConfig] = useState(null);
    const [gameState, setGameState] = useState('WAITING_SCAN'); // 'WAITING_SCAN', 'WAITING_CONFIRMATION', 'REGISTERING'
    const [scannedCode, setScannedCode] = useState('');
    const [pendingCode, setPendingCode] = useState('');
    const [displayCode, setDisplayCode] = useState('');
    const [currentCount, setCurrentCount] = useState(0);
    const [welcomeMessage, setWelcomeMessage] = useState('Bienvenid@');
    const [soundToPlay, setSoundToPlay] = useState('');
    const [isWinner, setIsWinner] = useState(false);
    const [confettiList, setConfettiList] = useState([]);
    const [isManualInputOpen, setIsManualInputOpen] = useState(false);
    const [manualCode, setManualCode] = useState('');

    // Refs para acumular caracteres del escáner
    const scanBufferRef = useRef('');
    const lastKeyTimeRef = useRef(0);
    const stateChangedTimeRef = useRef(0);

    const CONFETTI_COLORS = ['#ff003c', '#00ff41', '#fffb00', '#00e1ff', '#b300ff', '#ff8800', '#ffffff'];

    // Cargar configuración inicial y conectar WS para actualizaciones en vivo
    useEffect(() => {
        const fetchConfig = async () => {
            const res = await GetConfig();
            if (res) {
                setConfig(res);
            }
        };

        fetchConfig();

        const connectWS = async () => {
            const overlayType = `attendance:screen:1`;
            await ConnectWebSocketForOverlay(
                overlayType,
                (status) => console.log(`[AttendanceScreen WS Status] ${status}`),
                (toastMsg) => console.log(`[AttendanceScreen WS] ${toastMsg}`)
            );
            SetSocketMessageHandler(handleIncomingMessage);
        };

        connectWS();

        return () => {
            ClearSocketMessageHandler();
            DisconnectWebSocket();
        };
    }, []);

    const handleIncomingMessage = (message) => {
        if (!message || !message.type) return;
        const msgType = message.type.toLowerCase();
        if (msgType === 'attendanceconfigupdate') {
            const newConf = message.payload || message.data;
            if (newConf) {
                setConfig(prev => {
                    if (!prev) return newConf;
                    return {
                        ...prev,
                        waitingTitle: newConf.waitingTitle,
                        welcomeTitle: newConf.welcomeTitle,
                        winnerMessageTemplate: newConf.winnerMessageTemplate,
                        requireScan: newConf.requireScan,
                        winnerSelectionMode: newConf.winnerSelectionMode,
                        exactWinnerPositions: newConf.exactWinnerPositions,
                        winnerFrequency: newConf.winnerFrequency,
                        nextClickIsWinner: newConf.nextClickIsWinner,
                        messageDuration: newConf.messageDuration
                    };
                });
            }
        }
    };

    // Escuchar eventos de teclado para escáner y botón Enter
    useEffect(() => {
        const handleKeyDown = async (e) => {
            if (isManualInputOpen) return;

            const now = Date.now();
            const isRequireScan = config ? config.requireScan : true;

            // Si presionan la tecla M (caso insensible) en estado de espera, abrir modal de entrada manual
            if ((e.key === 'm' || e.key === 'M') && gameState === 'WAITING_SCAN' && isRequireScan) {
                e.preventDefault();
                setIsManualInputOpen(true);
                setManualCode('');
                return;
            }

            // Si estábamos en pantalla de ganador/bienvenida, cualquier tecla limpia y resetea el estado
            if (gameState === 'REGISTERING') {
                const elapsed = now - stateChangedTimeRef.current;
                if (elapsed < 1500) {
                    // Ignorar la tecla durante los primeros 1.5s para evitar resets involuntarios (rebote/bouncing/autotepetir)
                    return;
                }
                setGameState('WAITING_SCAN');
                setPendingCode('');
                setDisplayCode('');
                setConfettiList([]);
                setIsWinner(false);
                scanBufferRef.current = '';
                // No retornamos para que la tecla sea procesada normalmente en el estado WAITING_SCAN
            }

            // Lógica para detectar escáner de código de barras (entrada rápida de texto)
            const timeDiff = now - lastKeyTimeRef.current;
            lastKeyTimeRef.current = now;

            // Si es un caracter imprimible, lo agregamos al buffer
            if (e.key.length === 1) {
                if (timeDiff > 50) {
                    // Si pasa mucho tiempo, probablemente es entrada manual o el inicio del escaneo
                    scanBufferRef.current = e.key;
                } else {
                    scanBufferRef.current += e.key;
                }
                return;
            }

            // Si presionan Enter
            if (e.key === 'Enter') {
                e.preventDefault();

                // Caso A: Hay algo en el buffer del escáner (Lectura de abono)
                if (scanBufferRef.current.length > 0 && isRequireScan) {
                    const code = scanBufferRef.current;
                    scanBufferRef.current = ''; // Limpiar buffer
                    setPendingCode(code);
                    
                    // Mostrar los últimos 3 dígitos
                    const masked = code.length > 3 ? `xxxx${code.substring(code.length - 3)}` : code;
                    setDisplayCode(masked);
                    setGameState('WAITING_CONFIRMATION');
                    return;
                }

                // Caso B: Presionan Enter físico (Botón Antiestrés)
                if (gameState === 'WAITING_CONFIRMATION' && isRequireScan) {
                    // Registrar el código pendiente
                    triggerRegistration(pendingCode);
                } else if (!isRequireScan && gameState === 'WAITING_SCAN') {
                    // Modo Express: Registro directo
                    triggerRegistration('ANONIMO');
                }
            }

            // Teclas auxiliares de operador para corrección/limpieza
            if (e.key === 'Escape' || e.key === 'Backspace') {
                if (gameState === 'WAITING_CONFIRMATION') {
                    scanBufferRef.current = '';
                    setPendingCode('');
                    setDisplayCode('');
                    setGameState('WAITING_SCAN');
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [config, gameState, pendingCode, isManualInputOpen]);

    const triggerRegistration = async (code) => {
        setGameState('REGISTERING');
        stateChangedTimeRef.current = Date.now();
        
        const res = await RegisterAttendance(code);
        if (res && res.success) {
            setCurrentCount(res.attendanceNumber);
            setIsWinner(res.isWinner);
            setWelcomeMessage(res.message);
            
            // Sonido
            if (res.soundUrl) {
                reproducirSonido(getMediaUrl(res.soundUrl));
            } else if (res.isWinner) {
                // Sintetizar si no hay archivo
                reproducirSintetizado(0); // Campana para ganador
            } else {
                reproducirSintetizado(1); // Pollo/squeak para normal
            }

            // Confeti si gana
            if (res.isWinner) {
                generateConfetti();
            }

            // Retorno al estado de espera tras los segundos configurados en el Admin (MessageDuration)
            const duration = ((res && res.messageDuration) || (config && config.messageDuration) || 5) * 1000;
            setTimeout(() => {
                setGameState('WAITING_SCAN');
                setPendingCode('');
                setDisplayCode('');
                setConfettiList([]);
                setIsWinner(false);
            }, duration);
        } else {
            // Error en registro, restaurar
            setGameState('WAITING_SCAN');
        }
    };

    const reproducirSonido = (url) => {
        try {
            const audio = new Audio(url);
            audio.play().catch(e => console.warn("Error playing audio: ", e));
        } catch (e) {
            console.warn(e);
        }
    };

    const reproducirSintetizado = (index) => {
        try {
            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            const ctx = new AudioContextClass();
            const now = ctx.currentTime;
            if (index === 0) {
                // Campana
                [880, 1760].forEach((freq, i) => {
                    const osc = ctx.createOscillator();
                    const gain = ctx.createGain();
                    osc.frequency.setValueAtTime(freq, now);
                    gain.gain.setValueAtTime(0.2 / (i + 1), now);
                    gain.gain.exponentialRampToValueAtTime(0.0001, now + 1.2);
                    osc.connect(gain);
                    gain.connect(ctx.destination);
                    osc.start(now);
                    osc.stop(now + 1.2);
                });
            } else {
                // Pollo squeak
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = 'sawtooth';
                osc.frequency.setValueAtTime(350, now);
                osc.frequency.exponentialRampToValueAtTime(1000, now + 0.15);
                gain.gain.setValueAtTime(0.2, now);
                gain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);
                osc.connect(gain);
                gain.connect(ctx.destination);
                osc.start(now);
                osc.stop(now + 0.25);
            }
        } catch (e) {
            console.warn(e);
        }
    };

    const generateConfetti = () => {
        const list = [];
        for (let i = 0; i < 40; i++) {
            const startLeft = Math.random() * 100;
            const size = Math.random() * 12 + 10;
            const duration = Math.random() * 1.5 + 2.5;
            const delay = Math.random() * 1.5;
            const drift = Math.random() * 20 - 10;
            const rotation = Math.random() * 720 + 360;
            const color = CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)];

            list.push({
                id: i,
                style: {
                    '--left': `${startLeft}vw`,
                    '--size': `${size}px`,
                    '--color': color,
                    '--duration': `${duration}s`,
                    '--delay': `${delay}s`,
                    '--drift': `${drift}vw`,
                    '--rotation': `${rotation}deg`
                }
            });
        }
        setConfettiList(list);
    };

    const getMediaUrl = (path) => {
        if (!path) return '';
        if (path.startsWith('http://') || path.startsWith('https://')) return path;
        const apiBase = (process.env.REACT_APP_API_URL || '').replace('/api/', '/').replace('/api', '/');
        const url = `${apiBase}${path}`;
        return url.replace(/([^:]\/)\/+/g, "$1");
    };

    // Estilos responsivos
    const designStyles = `
        .attendance-body {
            color: #ffffff;
            font-family: 'Fredoka', sans-serif;
            z-index: 1;
            height: 100vh;
            width: 100vw;
            margin: 0;
            padding: 0;
            overflow: hidden;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            user-select: none;
            position: relative;
        }

        .brand-logo-img {
            position: absolute;
            top: 4vh;
            max-height: 10vh;
            max-width: 80%;
            object-fit: contain;
            filter: drop-shadow(0 5px 15px rgba(0,0,0,0.5));
            z-index: 20;
        }

        .attendance-bg-media {
            position: absolute;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            object-fit: cover;
            z-index: -1;
            pointer-events: none;
        }

        /* Estructura responsiva de personajes y ticket */
        .layout-container {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 2rem;
            width: 95%;
            max-width: 1200px;
            z-index: 10;
            position: relative;
            transition: all 0.5s ease;
        }

        .sponsor-char {
            max-height: 45vh;
            max-width: 22vw;
            object-fit: contain;
            filter: drop-shadow(0 15px 30px rgba(0,0,0,0.6));
            transition: all 0.5s ease;
        }

        /* Animaciones para que los personajes entren desde los lados */
        .slide-left {
            animation: slideLeftAnim 0.8s cubic-bezier(0.25, 1, 0.5, 1) forwards;
        }

        .slide-right {
            animation: slideRightAnim 0.8s cubic-bezier(0.25, 1, 0.5, 1) forwards;
        }

        @keyframes slideLeftAnim {
            0% { transform: translateX(-100vw); opacity: 0; }
            100% { transform: translateX(0); opacity: 1; }
        }

        @keyframes slideRightAnim {
            0% { transform: translateX(100vw); opacity: 0; }
            100% { transform: translateX(0); opacity: 1; }
        }

        /* Contenedor del Ticket */
        .ticket-wrapper {
            position: relative;
            flex-grow: 1;
            max-width: 700px;
            height: 250px;
            display: flex;
            justify-content: center;
            align-items: center;
            perspective: 1000px;
        }

        .ticket-glass {
            width: 100%;
            height: 100%;
            // background: rgba(0, 17, 51, 0.85);
            background: #a98219;
            border: 2px solid rgba(255, 255, 255, 0.1);
            border-radius: 20px;
            box-shadow: 0 20px 50px rgba(0,0,0,0.8);
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            padding: 2rem;
            text-align: center;
            transition: transform 0.6s cubic-bezier(0.25, 1, 0.5, 1), background-color 0.5s;
            z-index: 5;
            overflow: hidden;
        }

        /* Efecto de ticket troquelado */
        .ticket-glass::before, .ticket-glass::after {
            content: '';
            position: absolute;
            top: 50%;
            width: 24px;
            height: 24px;
            background: #050508;
            border-radius: 50%;
            transform: translateY(-50%);
            z-index: 10;
        }
        .ticket-glass::before { left: -12px; }
        .ticket-glass::after { right: -12px; }

        /* Estilos del Ticket en Modo Registro (Animación 3D) */
        .ticket-registering {
            animation: ticketFlip 4.5s cubic-bezier(0.25, 1, 0.5, 1) forwards;
            // background: linear-gradient(135deg, #001133 0%, #7E0200 100%);
            background: linear-gradient(135deg, #a98219 0%, #f5cc22 100%);
        }

        @keyframes ticketFlip {
            0% { transform: scale(0.8) rotateY(-90deg); opacity: 0; }
            15% { transform: scale(1.05) rotateY(10deg); opacity: 1; }
            20% { transform: scale(1) rotateY(0); }
            85% { transform: scale(1) rotateY(0); opacity: 1; }
            100% { transform: scale(0.8) rotateY(90deg); opacity: 0; }
        }

        .ticket-title {
            font-size: 2.8rem;
            font-weight: 700;
            line-height: 1.2;
            margin: 0;
            color: #ffffff;
            text-shadow: 0 5px 15px rgba(0,0,0,0.5);
        }

        .pulse-text {
            font-size: 3.5rem;
            font-weight: 700;
            line-height: 1.1;
            text-align: center;
            color: #ffffff;
            animation: breathe 3s infinite ease-in-out;
            text-shadow: 0 5px 15px rgba(0,0,0,0.8);
        }

        @keyframes breathe {
            0%, 100% { transform: scale(0.98); opacity: 0.9; }
            50% { transform: scale(1.02); opacity: 1; }
        }

        /* Mensajes de Confirmación */
        .welcome-subtitle {
            font-size: 1.2rem;
            letter-spacing: 0.15em;
            color: #FFD700;
            text-transform: uppercase;
            font-weight: 600;
            margin-bottom: 0.5rem;
        }

        /* CONFETTI CSS */
        .confetti-container {
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            pointer-events: none;
            z-index: 99;
            overflow: hidden;
        }

        .confetti-piece {
            position: absolute;
            width: var(--size);
            height: calc(var(--size) * 0.45);
            background-color: var(--color);
            top: -20px;
            left: var(--left);
            opacity: 0;
            transform-style: preserve-3d;
            animation: fall-confetti var(--duration) linear var(--delay) infinite;
        }

        @keyframes fall-confetti {
            0% {
                transform: translate3d(0, 0, 0) rotate3d(1, 1, 1, 0deg);
                opacity: 0;
            }
            10% { opacity: 1; }
            90% { opacity: 1; }
            100% {
                transform: translate3d(var(--drift), 105vh, 0) rotate3d(1.5, 2, 1, var(--rotation));
                opacity: 0;
            }
        }

        /* ==========================================================================
           RESPONSIVIDAD: MODO RETRATO / VERTICAL (PANTALLA DE 32 PULGADAS ROTADA)
           ========================================================================== */
        @media (orientation: portrait) {
            .layout-container {
                flex-direction: column;
                justify-content: center;
                gap: 3vh;
            }

            .sponsor-char {
                max-height: 25vh;
                max-width: 50vw;
            }

            .ticket-wrapper {
                width: 90%;
                height: 220px;
            }

            .ticket-title {
                font-size: 2.2rem;
            }

            .pulse-text {
                font-size: 2.6rem;
            }
        }
    `;

    // Carga de imágenes configuradas o por defecto
    const logoUrl = config && config.brandLogoUrl && config.brandLogoUrl.trim() !== "" ? getMediaUrl(config.brandLogoUrl) : '/Registro de asistencia/logo.png';
    const charLeftUrl = config && config.charLeftUrl && config.charLeftUrl.trim() !== "" ? getMediaUrl(config.charLeftUrl) : '/Registro de asistencia/1.png';
    const charRightUrl = config && config.charRightUrl && config.charRightUrl.trim() !== "" ? getMediaUrl(config.charRightUrl) : '/Registro de asistencia/2.png';

    const bgType = config ? config.backgroundType : 'color';
    const bgUrl = config ? config.backgroundUrl : '';
    const bgColor = config ? config.backgroundColor : '#050508';

    const bodyStyle = {
        backgroundColor: bgType === 'color' ? bgColor : 'transparent'
    };

    return (
        <div className="attendance-body" style={bodyStyle}>
            <style>{designStyles}</style>

            {/* Renderizado de Fondo Multimedia */}
            {bgType === 'image' && bgUrl && bgUrl.trim() !== "" && (
                <img src={getMediaUrl(bgUrl)} className="attendance-bg-media" alt="Background" />
            )}
            {bgType === 'video' && bgUrl && bgUrl.trim() !== "" && (
                <video src={getMediaUrl(bgUrl)} className="attendance-bg-media" autoPlay loop muted playsInline />
            )}

            {/* Logo Superior */}
            <img src={logoUrl} className="brand-logo-img" alt="Logo Club" onError={(e) => e.target.style.display = 'none'} />

            {/* Layout Principal de Personajes y Ticket */}
            <div className="layout-container">
                
                {/* Personaje Izquierdo (solo se muestra en espera o confirmación) */}
                {gameState !== 'REGISTERING' && (
                    <img 
                        src={charLeftUrl} 
                        className="sponsor-char slide-left" 
                        alt="Mascota Izquierda" 
                        onError={(e) => e.target.style.display = 'none'} 
                    />
                )}

                {/* Contenedor del Ticket */}
                <div className="ticket-wrapper">
                    
                    {/* ESTADO 1: ESPERANDO ESCANEO */}
                    {gameState === 'WAITING_SCAN' && (
                        <div className="ticket-glass">
                            <p className="pulse-text">
                                {config ? config.waitingTitle : 'REGISTRA TU ASISTENCIA'}
                            </p>
                        </div>
                    )}

                    {/* ESTADO 2: CONFIRMACIÓN PENDIENTE */}
                    {gameState === 'WAITING_CONFIRMATION' && (
                        <div className="ticket-glass" style={{ border: '2px solid #FFD700' }}>
                            <p className="welcome-subtitle">Abono Escaneado</p>
                            <h2 className="ticket-title" style={{ fontSize: '2.2rem', color: '#FFD700' }}>
                                Bienvenido abonado {displayCode}
                            </h2>
                            <p className="small text-muted mt-2" style={{ letterSpacing: '1px' }}>
                                Presiona el botón para registrar tu asistencia
                            </p>
                        </div>
                    )}

                    {/* ESTADO 3: REGISTRANDO Y ANIMANDO */}
                    {gameState === 'REGISTERING' && (
                        <div className={`ticket-glass ticket-registering`}>
                            {currentCount > 0 && (
                                <p className="welcome-subtitle" style={{ color: isWinner ? '#FFD700' : '#ffffff' }}>
                                    Registro #{currentCount}
                                </p>
                            )}
                            <h2 className="ticket-title" style={{ fontSize: isWinner ? '2.4rem' : '3rem' }}>
                                {welcomeMessage}
                            </h2>
                        </div>
                    )}

                </div>

                {/* Personaje Derecho (solo se muestra en espera o confirmación) */}
                {gameState !== 'REGISTERING' && (
                    <img 
                        src={charRightUrl} 
                        className="sponsor-char slide-right" 
                        alt="Mascota Derecha" 
                        onError={(e) => e.target.style.display = 'none'} 
                    />
                )}

            </div>

            {/* Confeti CSS para Ganador */}
            {isWinner && confettiList.length > 0 && (
                <div className="confetti-container">
                    {confettiList.map(item => (
                        <div key={item.id} className="confetti-piece" style={item.style} />
                    ))}
                </div>
            )}

            {/* Botón flotante discreto para Entrada Manual (para laptop/operador) */}
            {gameState === 'WAITING_SCAN' && !isManualInputOpen && (
                <button
                    onClick={() => {
                        setIsManualInputOpen(true);
                        setManualCode('');
                    }}
                    style={{
                        position: 'fixed',
                        bottom: '15px',
                        right: '15px',
                        backgroundColor: 'rgba(255,255,255,0.05)',
                        border: 'none',
                        color: 'rgba(255,255,255,0.2)',
                        borderRadius: '50%',
                        width: '35px',
                        height: '35px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        zIndex: 50
                    }}
                    title="Entrada Manual (M)"
                >
                    <i className="bi bi-keyboard-fill"></i>
                </button>
            )}

            {/* Modal de Entrada Manual */}
            {isManualInputOpen && (
                <div 
                    className="manual-input-modal" 
                    style={{
                        position: 'fixed',
                        top: 0, left: 0, width: '100vw', height: '100vh',
                        backgroundColor: 'rgba(0,0,0,0.85)',
                        display: 'flex', justifyContent: 'center', alignItems: 'center',
                        zIndex: 1000,
                        fontFamily: 'Fredoka, sans-serif'
                    }}
                >
                    <div className="bg-dark p-4 rounded-4 border border-secondary text-center" style={{ width: '90%', maxWidth: '400px' }}>
                        <h4 className="text-white mb-3"><i className="bi bi-keyboard text-warning"></i> Entrada Manual de Abono</h4>
                        <p className="small text-muted mb-4">Ingresa el código del abonado para iniciar el flujo de confirmación</p>
                        
                        <input 
                            type="text" 
                            className="form-control text-center fs-4 mb-4 bg-black text-white border-secondary"
                            autoFocus
                            placeholder="Escribe el código..."
                            value={manualCode}
                            onChange={(e) => setManualCode(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    if (manualCode.trim()) {
                                        setPendingCode(manualCode);
                                        const masked = manualCode.length > 3 ? `xxxx${manualCode.substring(manualCode.length - 3)}` : manualCode;
                                        setDisplayCode(masked);
                                        setGameState('WAITING_CONFIRMATION');
                                        setIsManualInputOpen(false);
                                        setManualCode('');
                                    }
                                } else if (e.key === 'Escape') {
                                    setIsManualInputOpen(false);
                                    setManualCode('');
                                }
                            }}
                        />

                        <div className="d-flex gap-2 justify-content-center">
                            <button 
                                className="btn btn-outline-secondary px-4 fw-bold"
                                onClick={() => {
                                    setIsManualInputOpen(false);
                                    setManualCode('');
                                }}
                            >
                                Cancelar
                            </button>
                            <button 
                                className="btn btn-primary px-4 fw-bold"
                                disabled={!manualCode.trim()}
                                onClick={() => {
                                    setPendingCode(manualCode);
                                    const masked = manualCode.length > 3 ? `xxxx${manualCode.substring(manualCode.length - 3)}` : manualCode;
                                    setDisplayCode(masked);
                                    setGameState('WAITING_CONFIRMATION');
                                    setIsManualInputOpen(false);
                                    setManualCode('');
                                }}
                            >
                                Aceptar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <p style={{ position: 'fixed', bottom: '15px', left: '50%', transform: 'translateX(-50%)', fontSize: '14px', color: 'rgba(255,255,255,0.3)', margin: 0 }}>
                Hecho por Interaktia
            </p>
        </div>
    );
};

export default AttendanceScreen;
